import { z } from "zod";

export type ScoreSheetFieldName =
	| "literature_review_score"
	| "design_method_score"
	| "presentation_score"
	| "discussion_score"
	| "clarity_score"
	| "comments_score";

export const submitScoreSheetSchema = z.object({
	literature_review_score: z.coerce.number({ error: "Invalid literature review score" }).int().min(1, { error: "Literature review score must be a positive integer" }).max(15),
	design_method_score: z.coerce.number({ error: "Invalid design method score" }).int().min(1, { error: "Design method score must be a positive integer" }).max(20),
	presentation_score: z.coerce.number({ error: "Invalid presentation score" }).int().min(1, { error: "Presentation score must be a positive integer" }).max(20),
	discussion_score: z.coerce.number({ error: "Invalid discussion score" }).int().min(1, { error: "Discussion score must be a positive integer" }).max(30),
	clarity_score: z.coerce.number({ error: "Invalid clarity score" }).int().min(1, { error: "Clarity score must be a positive integer" }).max(10),
	comments_score: z.coerce.number({ error: "Invalid comments score" }).int().min(1, { error: "Comments score must be a positive integer" }).max(5),
	general_comments: z.string().max(5000, "General comments must not exceed 5000 characters").default(""),
});

export type SubmitScoreSheetFormValues = z.input<typeof submitScoreSheetSchema>;
export type SubmitScoreSheetData = z.infer<typeof submitScoreSheetSchema>;

export type ExaminerAssignmentSection = {
	key: string;
	section: string;
	label: string;
	max_score: number;
	field: ScoreSheetFieldName;
};

export type ExaminerScoreSheet = {
	id: number;
	key: string;
	dissertation_id: number;
	examiner_assignment_id: number;
	examiner_id: number;
	literature_review_score: string;
	design_method_score: string;
	presentation_score: string;
	discussion_score: string;
	clarity_score: string;
	comments_score: string;
	general_comments: string | null;
	total_score: string;
	status: number;
	status_name: string;
	submitted_at: string | null;
};

export type ExaminerProfile = {
	id: number;
	key: string;
	full_name: string;
	email: string;
	mobile: string | null;
	type: string;
	institution: string | null;
	is_active: boolean;
	faculty?: {
		id: number | string | null;
		name: string | null;
	} | null;
	created_at: string;
	updated_at: string;
};

export type ExaminerAssignmentResponse = {
	success: boolean;
	message: string;
	data?: {
		assignment: {
			id: number;
			key: string;
			examiner_type: string;
			type_name: string;
			deadline: string | null;
			status: string;
			status_name: string;
			is_overdue: boolean;
		};
		dissertation: {
			id: number;
			key: string;
			title: string;
			abstract: string;
			status: number;
			status_name: string;
			faculty?: {
				id: number | string | null;
				name: string | null;
			} | null;
			candidate?: {
				id: number | string | null;
				name: string | null;
			} | null;
		};
		examiner?: ExaminerProfile | null;
		score_sheet_submitted: boolean;
		score_sheet: ExaminerScoreSheet | null;
		sections: ExaminerAssignmentSection[];
	};
};

export type ExaminerFeedbackItem = {
	id: number | string;
	key: string;
	status: number | string;
	status_name: string;
	comment: string;
	has_file: boolean;
	file_url: string | null;
	original_file_name: string | null;
	attachments?: {
		id: number | string;
		file_url: string;
		original_file_name: string;
	}[];
	is_read: boolean;
	read_at: string | null;
	user?: {
		name: string;
		email: string;
	} | null;
	dissertation: {
		id: number | string | null;
		key: string | null;
		title: string | null;
	};
	created_at: string;
	updated_at: string;
};

export type ExaminerFeedbackResponse = {
	success: boolean;
	message: string;
	data: ExaminerFeedbackItem[];
	metadata?: {
		current_page?: number;
		last_page?: number;
		per_page?: number;
		total?: number;
	};
};
